import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MenuItem, Stack, TextField, Button, FormControl, Select, InputLabel, FormControlLabel, Switch } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Page from '../../data/Page';
import { getUser, updateUser } from '../../services/Common/user.service';
import { getAdminUserWallet, updateWalletBalance } from '../../services/Common/wallet.service';
import { toast } from 'react-toastify';
import { useSelector } from "react-redux"


export default function UserForm({ edit }) {
    const { isAuthenticated } = useSelector(state => state.auth)
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        role: "",
        profileImage: "",
        is_verified: false
    })
    const [loading, setLoading] = useState(false)
    const [walletLoading, setWalletLoading] = useState(false)
    const [walletForm, setWalletForm] = useState({ type: 'ev', amount: '', reason: '' })
    const [currentWallet, setCurrentWallet] = useState({ ev: 0, trp: 0 })
    const [walletDetails, setWalletDetails] = useState(null)

    const { name, email, role, profileImage, is_verified } = formData
    const { type, amount, reason } = walletForm

    useEffect(() => {
        if (!isAuthenticated) return;
        const getUserDetails = async () => {
            try {
                const { data: user } = await getUser(id);
                const adminWallet = await getAdminUserWallet(id);
                console.log(user)
                setFormData({
                    name: user[0].name,
                    email: user[0].email,
                    role: user[0].role[0],
                    is_verified: user[0].is_verified === true
                })
                // If the API returns wallet on user, capture it (fallback to zeros)
                const w = user[0]?.wallet || {}
                setCurrentWallet({ ev: Number(w.ev) || 0, trp: Number(w.trp) || 0 })
                setWalletDetails(adminWallet)
                if (adminWallet?.balance) setCurrentWallet(adminWallet.balance)
            } catch (err) {
                console.log(err)
                toast("Error getting the user details. Please refresh the page. ")
            }
        };

        if (id)
            getUserDetails();

    }, [id, isAuthenticated]);

    const onChange = (e) => {
        const name = e.target.name

        if (name === 'profileImage')
            return setFormData({ ...formData, [name]: e.target.files[0] })

        setFormData({ ...formData, [name]: e.target.value })
    }

    const onSubmit = async () => {
        setLoading(true)
        try {
            const payload = new FormData();

            payload.append("name", name)
            payload.append("email", email)
            payload.append("profileImage", profileImage)
            payload.append('role', role)
            payload.append('is_verified', String(is_verified))

            await updateUser(id, payload)
            toast("User successfully updated.")
        } catch (err) {
            console.log(err.message)
            toast("Some error occoured please try again.")
        }
        setLoading(false)
    }

    const onWalletUpdate = async () => {
        if (!id) return toast('User not loaded yet', { type: 'error' })
        if (!amount || isNaN(Number(amount))) return toast('Enter a valid amount', { type: 'error' })
        try {
            setWalletLoading(true)
            await updateWalletBalance({ userId: id, type, amount: Number(amount), reason })
            toast('Wallet updated successfully.', { type: 'success' })
            // Optimistically update displayed balance
            setCurrentWallet(prev => ({
                ...prev,
                [type]: (Number(prev[type]) || 0) + Number(amount)
            }))
            setWalletForm({ type: 'ev', amount: '', reason: '' })
        } catch (err) {
            console.log(err)
            toast(err?.response?.data?.message || 'Failed to update wallet', { type: 'error' })
        }
        setWalletLoading(false)
    }


    return (
        <>
            <Stack spacing={3}>
                <TextField
                    name="name"
                    label="Name"
                    variant="filled"
                    size="small"
                    value={name}
                    required
                    onChange={onChange}
                />
                <TextField
                    name="email"
                    type="email"
                    label="Email Address"
                    variant="filled"
                    size="small"
                    value={email}
                    required
                    onChange={onChange}
                />
                <FormControl>
                    <InputLabel id="account_type_select">Select User Type</InputLabel>
                    <Select labelId="account_type_select"
                        name="role"
                        value={role}
                        onChange={onChange}>
                        <MenuItem value={"user"}>Normal Account</MenuItem>
                        <MenuItem value={"organization"}>Organizer Account</MenuItem>
                        <MenuItem value={"admin"}>Admin Account</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Switch name="is_verified" checked={is_verified} onChange={(event) => setFormData({ ...formData, is_verified: event.target.checked })} />}
                    label="Verified profile"
                />
                <TextField
                    name="profileImage"
                    label="Upload Image"
                    type="file"
                    onChange={onChange}
                    variant="filled"
                    size="small"
                    required={edit ? false : true}
                />
                <Stack direction="row" gap={2}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="large"
                        color="primary"
                        loading={loading}
                        onClick={onSubmit}
                    >
                        Submit
                    </LoadingButton>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={() => navigate(Page.USER)}
                    >
                        Go Back
                    </Button>
                </Stack>
            </Stack >
            <Stack spacing={2} sx={{ mt: 5 }}>
                <h3>Wallet Adjustment</h3>
                <Stack direction="row" spacing={3}>
                    <div>
                        <strong>Current EV:</strong> {currentWallet.ev}
                    </div>
                    <div>
                        <strong>Current TRP:</strong> {currentWallet.trp}
                    </div>
                </Stack>
                <FormControl>
                    <InputLabel id="wallet_type_select">Currency</InputLabel>
                    <Select labelId="wallet_type_select" name="type" value={type} onChange={(e) => setWalletForm(prev => ({ ...prev, type: e.target.value }))}>
                        <MenuItem value={'ev'}>EV</MenuItem>
                        <MenuItem value={'trp'}>TRP</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    name="amount"
                    type="number"
                    label="Amount (use negative to deduct)"
                    variant="filled"
                    size="small"
                    value={amount}
                    onChange={(e) => setWalletForm(prev => ({ ...prev, amount: e.target.value }))}
                />
                <TextField
                    name="reason"
                    label="Reason (optional)"
                    variant="filled"
                    size="small"
                    value={reason}
                    onChange={(e) => setWalletForm(prev => ({ ...prev, reason: e.target.value }))}
                />
                <LoadingButton
                    variant="outlined"
                    color="primary"
                    loading={walletLoading}
                    onClick={onWalletUpdate}
                >
                    Apply Wallet Update
                </LoadingButton>
            </Stack>
            {walletDetails && <Stack spacing={2} sx={{ mt: 5 }}>
                <h3>Wallet History</h3>
                <Stack direction="row" spacing={3}>
                    <div><strong>Total:</strong> {Number(walletDetails.balance?.ev || 0) + Number(walletDetails.balance?.trp || 0)}</div>
                    <div><strong>EV Coins:</strong> {walletDetails.balance?.ev || 0}</div>
                    <div><strong>Trooper Coins:</strong> {walletDetails.balance?.trp || 0}</div>
                </Stack>
                <div>
                    <strong>EV Activity Distribution:</strong>{' '}
                    {Object.entries(walletDetails.evDistribution || {}).map(([source, value]) => `${source}: ${value}`).join(' · ') || 'No earnings'}
                </div>
                <div>
                    <strong>Trooper Tournament Distribution:</strong>{' '}
                    {Object.entries(walletDetails.trpDistribution || {}).map(([source, value]) => `${source}: ${value}`).join(' · ') || 'No earnings'}
                </div>
                <h4>Earning History</h4>
                {walletDetails.earningHistory?.length ? walletDetails.earningHistory.map(transaction => (
                    <div key={transaction._id}><strong>+{transaction.amount} {transaction.type?.toUpperCase()}</strong> — {transaction.reason || transaction.transactionType} — {transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '-'}</div>
                )) : <div>No earning history</div>}
                <h4>Spending History</h4>
                {walletDetails.spendingHistory?.length ? walletDetails.spendingHistory.map(transaction => (
                    <div key={transaction._id}><strong>-{transaction.amount} {transaction.type?.toUpperCase()}</strong> — {transaction.reason || transaction.transactionType} — {transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '-'}</div>
                )) : <div>No spending history</div>}
                <h4>Withdrawal History</h4>
                {walletDetails.withdrawalHistory?.length ? walletDetails.withdrawalHistory.map(withdrawal => (
                    <div key={withdrawal._id}><strong>{withdrawal.amount} {withdrawal.type?.toUpperCase()}</strong> — {withdrawal.status} — {withdrawal.createdAt ? new Date(withdrawal.createdAt).toLocaleString() : '-'}</div>
                )) : <div>No withdrawal history</div>}
            </Stack>}
        </>
    );
}
